import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import axios, { AxiosError } from "axios";
import { format, isSameDay, parseISO } from "date-fns";
import { useSnackbar } from "notistack";
import { ReactNode, memo, useCallback, useEffect, useState } from "react";
import { useQuery } from "react-query";
import { API_URL } from "../consts";
import {
  Cards,
  Clubs,
  Colori,
  Prenotazioni,
  RisorseAndRisorseTipologie,
  RisorseInfoCorrenti,
  Tariffe_Calendario,
  Tariffe_Costo,
  Tariffe_Definizioni,
} from "../prismaTypes";
import { ImpostaTariffaDialog } from "./ImpostaTariffaDialog";
import { SlotTariffa } from "./SlotTariffa";
import { it } from "date-fns/locale";
import Alert from "@mui/material/Alert";
import { Loading } from "../ui/Loading";
import React from "react";
import { gruppaOrari, Orari } from "../utils/gruppaOrari";
import { PrenotaState, prenotaStore } from "../prenotaStore";
import { useNavigate } from "react-router-dom";
import { UserState, oraPrenotaStore } from "../oraPrenotaStore";
import { ViewPrenotazione } from "../prenotazione/ViewPrenotazione";
import { Dialog, Tooltip, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { createDateAsUTC, dateToISOString, dateToUTCString, readDate } from "../utils/dateUtils";
import { queryClient } from "../App";
import { getSlots } from "./getSlots";
import { Slot } from "./Slot";
import { memoize } from "lodash-es";

export const MILL_PER_MIN = 60 * 1000;

const memoParseISO = memoize(parseISO);

const now = new Date();
const timeNow = now.getTime();

const getDiffNow = memoize(function getDiffNow(timeNowParam: number) {
  return memoize(function getDiff(g: Date) {
    const diffTime = Math.ceil(g.getTime() - timeNowParam);
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  });
});

const TabelloneHeader = React.memo(function (props: { giorni: Date[] }) {
  const { giorni } = props;

  return (
    <thead>
      <tr>
        <th></th>
        {giorni &&
          giorni.map((g) => (
            <th key={g.toString()} className="tabelloneGiorno">
              <Typography key={g.toISOString()} variant="caption" noWrap={true}>
                {format(g, "E dd/MM", { locale: it })}
              </Typography>
            </th>
          ))}
      </tr>
    </thead>
  );
});
const TabelloneGiorni = React.memo(function (props: { text: string }) {
  const { text } = props;

  return (
    <td>
      <Typography
        noWrap={true}
        variant="caption"
        align="center"
        sx={{ paddingRight: "10px", lineHeight: 0, letterSpacing: 0 }}
      >
        {text}
      </Typography>
    </td>
  );
});

const selectIsAuth = (state: UserState) => state.isAuthenticated();
const selectRole = (state: UserState) => state.role;

const selectSetGiorno = (state: PrenotaState) => state.setGiorno;
const selectSetRisorsa = (state: PrenotaState) => state.setRisorsa;
const selectSetOrario = (state: PrenotaState) => state.setOrario;
const selectSetSlotTariffe = (state: PrenotaState) => state.setSlotTariffe;

export const TabellaTariffe = memo(function TabellaTariffe(props: {
  risorsa: RisorseAndRisorseTipologie;
  giorni: Date[];
  modPrenota: boolean;
}) {
  const { risorsa, giorni, modPrenota } = props;
  const { enqueueSnackbar } = useSnackbar();

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const { data: club, isLoading: isLoadingClub } = useQuery<{ club: Clubs }, AxiosError>(
    ["club/getByDomain"],
    () => axios.get(API_URL + "club/getByDomain").then((res) => res.data),
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    }
  );

  const idAuth = oraPrenotaStore(selectIsAuth);
  const role = oraPrenotaStore(selectRole);
  const isAuthenticated = idAuth || window.location.href.includes("/prenotazione/");

  const [tariffe, setTariffe] = useState<{ [k: string]: SlotTariffa }>({});

  const [open, setOpen] = useState(false);
  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  let slots: Slot[] = [];

  const dayOne = giorni[0];
  useEffect(() => {
    setTariffe({});
  }, [dayOne]);

  const { isLoading, data: data2 } = useQuery<
    unknown,
    AxiosError,
    { risorseInfo: RisorseInfoCorrenti[] }
  >(
    ["risorse-info/getRisorseInfoActive", risorsa.id],
    () =>
      axios
        .get(API_URL + "risorse-info/getRisorseInfoActive/" + risorsa.id)
        .then((res) => res.data),
    {
      enabled: giorni !== undefined,
      refetchOnWindowFocus: false,
    }
  );

  const risorsaAttiva = data2?.risorseInfo.find(
    (r) =>
      r.inizioValidita !== null &&
      readDate(r.inizioValidita) <= giorni[0] &&
      r.fineValidita !== null &&
      readDate(r.fineValidita) >= giorni[giorni.length - 1]
  );

  const { isLoading: isLoading2, data } = useQuery<
    {
      tariffeCalendario: Tariffe_Calendario[];
    },
    AxiosError
  >(
    ["tariffe-calendario/findAll", risorsa.id],
    () => axios.get(API_URL + "tariffe-calendario/findAll/" + risorsa.id).then((res) => res.data),
    {
      refetchOnWindowFocus: false,
    }
  );

  const { data: prenotazioni } = useQuery<
    {
      prenotazioni: (Prenotazioni & {
        nome: string;
        cognome: string;
        partecipanti: string | null;
      })[];
    },
    AxiosError
  >(
    [
      "prenotazioni",
      risorsa.id,
      dateToISOString(createDateAsUTC(giorni[0])),
      dateToISOString(createDateAsUTC(giorni[giorni.length - 1])),
    ],
    () =>
      axios
        .get(
          API_URL +
            "prenotazioni/week/" +
            risorsa.id +
            "/" +
            dateToISOString(createDateAsUTC(giorni[0])) +
            "/" +
            dateToISOString(createDateAsUTC(giorni[giorni.length - 1]))
        )
        .then((res) => res.data),
    {
      enabled: modPrenota,
    }
  );

  if (risorsaAttiva && data?.tariffeCalendario)
    slots = getSlots(risorsaAttiva, data.tariffeCalendario);

  const [openView, setOpenView] = useState(false);
  const [idPrenotazione, setIdPrenotazione] = useState(0);

  const handleCloseView = useCallback(() => {
    setOpenView(false);
    setIdPrenotazione(-1);
  }, [setOpenView, setIdPrenotazione]);

  const visualizza = useCallback(
    (prenotato: Prenotazioni | undefined) => {
      if (!prenotato) return;
      setIdPrenotazione(prenotato.id);
      setOpenView(true);
    },
    [setIdPrenotazione, setOpenView]
  );

  const { isLoading: isLoading3, data: tariffeDef } = useQuery<
    {
      definizioni: (Tariffe_Definizioni & {
        colore: Colori;
      })[];
    },
    AxiosError
  >(
    ["tariffe-definizioni"],
    () => axios.get(API_URL + "tariffe-definizioni/findAll").then((res) => res.data),
    { refetchOnWindowFocus: false }
  );

  const { isLoading: isLoading7, data: card } = useQuery<
    {
      cards: (Cards & {
        idMembro: number;
        Tipologia_AssociazioniId: number;
        certificato: number;
        dataScadenzaCertificato: string;
        visibilitaGiorni: number;
      })[];
    },
    AxiosError
  >(["cards/findMyCard"], () => axios.get(API_URL + "cards/findMyCard").then((res) => res.data), {
    enabled: role === "USER",
  });

  const { isLoading: isLoadingCosti, data: costi } = useQuery<
    { tariffe: Tariffe_Costo[] },
    AxiosError
  >(
    ["tariffe-costo/findAll/"],
    () => axios.get(API_URL + "tariffe-costo/findAll/").then((res) => res.data),
    {
      enabled: club !== undefined && idAuth,
    }
  );

  let dataScadenza: Date | undefined = undefined;
  if (card?.cards[0]?.dataScadenzaCertificato !== undefined)
    dataScadenza = readDate(card?.cards[0]?.dataScadenzaCertificato);

  let dataScadenzaCard: Date | undefined = undefined;
  if (card?.cards[0]?.dtScadenza !== undefined)
    dataScadenzaCard = readDate(card?.cards[0]?.dtScadenza);

  const onClick =
    (
      idSlot: number,
      giorno: Date,
      tariffa:
        | (Tariffe_Definizioni & {
            colore: Colori;
          })
        | undefined,
      passato: boolean
    ) =>
    () => {
      if (!isAuthenticated) {
        enqueueSnackbar("Per prenotare è necessario effettuare il log in!", { variant: "warning" });
        return;
      }

      if (modPrenota && tariffa === undefined) {
        enqueueSnackbar("Slot non prenotabile!", { variant: "warning" });
        return;
      }

      if (passato && role === "USER") return;

      if (role === "USER") {
        if (
          !costi?.tariffe.some(
            (c) =>
              c.tariffe_DefinizioniId === tariffa?.id &&
              c.tipologia_AssociazioniId === card?.cards[0]?.Tipologia_AssociazioniId
          )
        ) {
          enqueueSnackbar("Non puoi prenotare questo campo!", { variant: "warning" });
          return;
        }
      }

      if (role === "USER" && (dataScadenzaCard === undefined || dataScadenzaCard <= giorno)) {
        enqueueSnackbar("Giocatore non attivo per questo giorno!", { variant: "warning" });
        return;
      }

      if (
        role === "USER" &&
        club?.club.certificatoObbligatorio &&
        (1 !== card?.cards[0]?.certificato || dataScadenza === undefined || dataScadenza <= giorno)
      ) {
        enqueueSnackbar("È necessario il certificato medico", { variant: "warning" });
        return;
      }

      const id = giorno.toUTCString() + "-" + idSlot;
      const selected = tariffe[id];
      if (selected) {
        const newTariffe = { ...tariffe };
        delete newTariffe[id];
        setTariffe(newTariffe);
      } else {
        const slot = slots[idSlot];

        const valori = Object.values(tariffe);
        for (const slotTariffa of valori) {
          if (giorno.getDay() !== slotTariffa.giorno.getDay()) {
            enqueueSnackbar("Selezionare slot di uno stesso giorno!", { variant: "error" });
            return;
          }
        }

        if (role === "USER" && risorsaAttiva?.massimoSlotPrenotabiliPerGiorno) {
          const slotEsistenti = (prenotazioni?.prenotazioni ?? [])
            .filter(
              (p) =>
                p.dataDisdetta === null &&
                p.membersId === card?.cards[0]?.idMembro &&
                isSameDay(readDate(p.dataInizioPrenotazione), giorno)
            )
            .reduce((sum, p) => sum + p.slotPrenotati, 0);

          const slotSelezionati = valori.length;

          if (
            slotEsistenti + slotSelezionati + 1 >
            risorsaAttiva.massimoSlotPrenotabiliPerGiorno
          ) {
            enqueueSnackbar(
              "Hai raggiunto il numero massimo di slot prenotabili per oggi su questo campo (max " +
                risorsaAttiva.massimoSlotPrenotabiliPerGiorno +
                ")",
              { variant: "error" }
            );
            return;
          }
        }

        setTariffe({
          ...tariffe,
          [id]: {
            giorno,
            oraInizio: format(slot.oraInizio, "HH:mm"),
            oraFine: format(slot.oraFine, "HH:mm"),
            oraInizioDate: slot.oraInizio,
            oraFineDate: slot.oraFine,
            tariffa,
          },
        });
      }
    };

  const isSelected = (idSlot: number, giorno: Date) =>
    !!tariffe[dateToUTCString(giorno) + "-" + idSlot];

  const setGiorno = prenotaStore(selectSetGiorno);
  const setRisorsa = prenotaStore(selectSetRisorsa);
  const setOrario = prenotaStore(selectSetOrario);
  const setSlotTariffe = prenotaStore(selectSetSlotTariffe);
  const navigate = useNavigate();

  const impostaTariffaClick = () => {
    if (!isAuthenticated) return;

    const numSlots = Object.keys(tariffe).length;
    if (numSlots < 1) {
      enqueueSnackbar("È necessario selezionare almeno uno slot!", { variant: "error" });
      return;
    }
    const valori = Object.values(tariffe);
    const giorno = valori[0].giorno;
    for (const slotTariffa of valori) {
      if (giorno.getDay() !== slotTariffa.giorno.getDay()) {
        enqueueSnackbar("Selezionare slot di uno stesso giorno!", { variant: "error" });
        return;
      }
    }

    if (!modPrenota) {
      handleClickOpen();
      return;
    }

    const orari: Orari[] = gruppaOrari(tariffe);
    if (orari.length > 1) {
      enqueueSnackbar("Gli slot devono essere contigui!", { variant: "error" });
      return;
    }

    if (role === "USER") {
      if (
        risorsaAttiva?.numeroMinimoSlotPrenotabili &&
        risorsaAttiva?.numeroMinimoSlotPrenotabili > numSlots
      ) {
        enqueueSnackbar(
          "Non è stato selezionato il numero minimo di slot (min " +
            risorsaAttiva?.numeroMinimoSlotPrenotabili +
            ")",
          { variant: "error" }
        );
        return;
      }

      if (
        risorsaAttiva?.numeroMassimoSlotPrenotabili &&
        risorsaAttiva?.numeroMassimoSlotPrenotabili < numSlots
      ) {
        enqueueSnackbar(
          "È stato superato il numero massimo di slot di slot (max " +
            risorsaAttiva?.numeroMassimoSlotPrenotabili +
            ")",
          { variant: "error" }
        );
        return;
      }
    }

    setGiorno(giorno);
    setRisorsa(risorsa);
    setOrario(orari[0]);
    setSlotTariffe(tariffe);
    navigate("/app/prenotazione");
  };

  const loading =
    isLoading || isLoading2 || isLoading3 || isLoading7 || isLoadingCosti || isLoadingClub;

  let finePostStr = "";
  let finePreStr = "";
  let inizioPostStr = "";
  let inizioPreStr = "";

  if (!loading && modPrenota) {
    const inizioPost = new Date(giorni[0]);
    inizioPost.setDate(inizioPost.getDate() + 7);
    inizioPost.setHours(0, 0, 0, 0);
    inizioPostStr = dateToISOString(createDateAsUTC(inizioPost));
    const finePost = new Date(giorni[giorni.length - 1]);
    finePost.setDate(finePost.getDate() + 7);
    finePost.setHours(0, 0, 0, 0);
    finePostStr = dateToISOString(createDateAsUTC(finePost));

    const inizioPre = new Date(giorni[0]);
    inizioPre.setDate(inizioPre.getDate() - 7);
    inizioPre.setHours(0, 0, 0, 0);
    inizioPreStr = dateToISOString(createDateAsUTC(inizioPre));
    const finePre = new Date(giorni[giorni.length - 1]);
    finePre.setDate(finePre.getDate() - 7);
    finePre.setHours(0, 0, 0, 0);
    finePreStr = dateToISOString(createDateAsUTC(finePre));
  }

  useEffect(() => {
    if (!loading && modPrenota) {
      const myInterval = window.setTimeout(() => {
        queryClient.prefetchQuery(["prenotazioni", risorsa.id, inizioPostStr, finePostStr], () =>
          axios
            .get(
              API_URL + "prenotazioni/week/" + risorsa.id + "/" + inizioPostStr + "/" + finePostStr
            )
            .then((res) => res.data)
        );
        queryClient.prefetchQuery(["prenotazioni", risorsa.id, inizioPreStr, finePreStr], () =>
          axios
            .get(
              API_URL + "prenotazioni/week/" + risorsa.id + "/" + inizioPreStr + "/" + finePreStr
            )
            .then((res) => res.data)
        );
      }, 500);
      return () => window.clearInterval(myInterval);
    }
  }, [inizioPostStr, finePostStr, modPrenota, risorsa.id, inizioPreStr, finePreStr, loading]);

  if (loading) return <Loading />;
  if (slots.length < 1) return <Alert severity="warning">Dettagli funzionali non trovati!</Alert>;

  if (club?.club === undefined) return <Alert severity="warning">Club non trovato!</Alert>;

  const oraInizioSlotZero = slots[0].oraInizio.getUTCHours();
  const oraFineSlotZero = slots[0].oraFine.getUTCHours();
  const oraIn = slots[0].oraInizio.getHours();
  const isAdmin = role === "ADMIN" || role === "BACKOFFICE" || role === "CLUB_ADMIN";

  return (
    <>
      <MyStack>
        <table className="tabellone">
          <TabelloneHeader giorni={giorni} />
          <tbody>
            {slots.map((s, i) => {
              const oraInizioSlot = s.oraInizio.getHours();
              const minutiInizioSlot = s.oraInizio.getUTCMinutes();

              const oraFineSlot = s.oraFine.getHours();
              const minutiFineSlot = s.oraFine.getUTCMinutes();

              return (
                <tr key={s.id + i.toString()}>
                  <TabelloneGiorni text={s.text} />
                  {giorni.map((g) => {
                    const dateOggi = new Date(g);
                    const sInizio = new Date(s.oraInizio).getHours();
                    const sFine = new Date(s.oraFine).getHours();

                    let prenotabile = true;

                    if (
                      role === "USER" &&
                      card?.cards[0].visibilitaGiorni !== 1 &&
                      club.club.giorniPrenotabiliNelFuturo > 0
                    ) {
                      const diffDays = getDiffNow(timeNow)(g);
                      if (diffDays > club.club.giorniPrenotabiliNelFuturo) prenotabile = false;
                    }

                    const oggi: number = g.getDay();

                    const tariffa = !prenotabile
                      ? undefined
                      : data?.tariffeCalendario.find((t) => {
                          const oraInizio = memoParseISO(t.oraInizio.slice(0, -1));
                          let oraFine = memoParseISO(t.oraFine.slice(0, -1));
                          let dataFine = memoParseISO(t.dataFine.slice(0, -1));
                          let dataInizio = memoParseISO(t.dataInizio.slice(0, -1));

                          if (oraInizio.getHours() < oraIn) {
                            oraInizio.setDate(oraInizio.getDate() + 1);
                            dataInizio.setDate(dataInizio.getDate() + 1);
                          }

                          if (oraFine.getHours() < oraIn) {
                            oraFine.setDate(oraFine.getDate() + 1);
                          }

                          if (dataFine < dateOggi) return false;
                          if (dataInizio > dateOggi) return false;

                          if (!t.domenica && oggi === 0) return false;
                          if (!t.lunedi && oggi === 1) return false;
                          if (!t.martedi && oggi === 2) return false;
                          if (!t.mercoledi && oggi === 3) return false;
                          if (!t.giovedi && oggi === 4) return false;
                          if (!t.venerdi && oggi === 5) return false;
                          if (!t.sabato && oggi === 6) return false;

                          if (oraInizio.getHours() <= sInizio && oraFine.getHours() >= sFine)
                            return true;

                          return false;
                        });

                    const tariffaApplicata = tariffeDef?.definizioni.find(
                      (d) => d.id === tariffa?.tariffe_DefinizioniId
                    );

                    const style =
                      tariffaApplicata === undefined
                        ? {}
                        : modPrenota
                        ? {
                            boxShadow:
                              "inset -15px 0px 0px -5px " + tariffaApplicata?.colore.esadecimale,
                          }
                        : { backgroundColor: tariffaApplicata?.colore.esadecimale };

                    const dataMid = new Date(g);
                    dataMid.setHours(s.oraInizio.getHours());
                    dataMid.setUTCMinutes(s.oraInizio.getUTCMinutes());
                    dataMid.setUTCSeconds(30);

                    if (s.oraFine.getUTCHours() < slots[0].oraInizio.getUTCHours())
                      dataMid.setDate(dataMid.getDate() + 1);

                    const prenotato = prenotazioni?.prenotazioni.find((p) => {
                      if (p.dataDisdetta !== null) return false;

                      const dataInizioPre = readDate(p.dataInizioPrenotazione);
                      const dataFinePre = readDate(p.dataFinePrenotazione);

                      const ret = dataMid >= dataInizioPre && dataMid < dataFinePre;

                      return ret;
                    });

                    const prenotatoDaMe =
                      prenotato &&
                      (prenotato?.membersId === card?.cards[0].idMembro ||
                        prenotato?.cardsId === card?.cards[0].id);

                    const inizioSlot = new Date(g);
                    inizioSlot.setHours(oraInizioSlot);
                    inizioSlot.setUTCMinutes(minutiInizioSlot);
                    if (s.oraFine.getUTCHours() < oraInizioSlotZero)
                      inizioSlot.setDate(inizioSlot.getDate() + 1);

                    const fineSlot = new Date(g);
                    fineSlot.setHours(oraFineSlot);
                    fineSlot.setUTCMinutes(minutiFineSlot);
                    if (s.oraFine.getUTCHours() < oraFineSlotZero)
                      fineSlot.setDate(fineSlot.getDate() + 1);

                    const passato = inizioSlot < now;
                    const passatoFine = fineSlot < now;

                    let tooltip = "";

                    if (modPrenota && prenotato && isAdmin) {
                      tooltip = prenotato.nome + " " + prenotato.cognome;
                      if (prenotato.partecipanti)
                        tooltip += " - Invitati: " + prenotato.partecipanti;
                    }

                    const cell = (
                      <td
                        key={"" + s.id + "-" + g.getTime()}
                        className={
                          "tdTariffa" +
                          (passato ? " passata" : "") +
                          (modPrenota && prenotatoDaMe && prenotabile ? " prenotatoDaMe" : "") +
                          (modPrenota && prenotato && !prenotatoDaMe && prenotabile
                            ? " prenotato"
                            : "") +
                          (modPrenota && tariffaApplicata !== undefined && !prenotato && prenotabile
                            ? " prenotabile"
                            : "") +
                          (isSelected(s.id, g) ? " slotSelected" : "")
                        }
                        onClick={() => {
                          if (modPrenota && (prenotatoDaMe || isAdmin)) visualizza(prenotato);
                          if ((!modPrenota || !prenotato) && prenotabile)
                            onClick(s.id, g, tariffaApplicata, passatoFine)();
                        }}
                        style={style}
                      ></td>
                    );

                    if (tooltip === "") return cell;

                    return (
                      <Tooltip title={tooltip} key={"" + s.id + "-" + g.getTime()}>
                        {cell}
                      </Tooltip>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>

        {isAuthenticated && (
          <Button onClick={impostaTariffaClick}>
            {modPrenota ? "Prenota" : "Imposta Tariffa"}
          </Button>
        )}

        {!modPrenota && (
          <ImpostaTariffaDialog
            key={JSON.stringify(tariffe)}
            open={open}
            risorseId={risorsa.id}
            handleClose={handleClose}
            slotsTariffe={tariffe}
            setSlotsTariffe={setTariffe}
          />
        )}
      </MyStack>
      {idPrenotazione > 0 && (
        <Dialog open={openView} onClose={handleCloseView} fullScreen={fullScreen}>
          <ViewPrenotazione
            key={idPrenotazione}
            handleClose={handleCloseView}
            id={idPrenotazione}
          />
        </Dialog>
      )}
    </>
  );
});

const mySx = { tr: { height: ["30px", "30px", "18px"] } };
function MyStack(props: { children: ReactNode }) {
  const { children } = props;
  return (
    <Stack gap={1} sx={mySx}>
      {children}
    </Stack>
  );
}
